import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import useAiQuota from '@/hooks/useAiQuota';
import {
  User, MapPin, Calendar, Stethoscope, BookOpen, ExternalLink,
  Plus, Trash2, Save, Loader2, CheckCircle, XCircle, Clock, AlertCircle, ToggleRight, Star, Sparkles,
  Camera, Upload, Wallet, UserX, Crown, CreditCard, Download, Lock, AlertTriangle, Copy, Share2, MessageCircle,
  FileText, Image, Send, SpellCheck, Bell, BellOff, BarChart3, TrendingUp, Eye, EyeOff, Phone, Search,
  Newspaper, History, Check, X, Edit3, RefreshCw, ChevronDown, QrCode, Zap, Gift, Ticket, ClipboardList
} from 'lucide-react';
import BookingChat from '@/components/BookingChat';
import PinSettings from '@/components/dashboard/PinSettings';
import ProfilePublishedCelebration from '@/components/dashboard/ProfilePublishedCelebration';
import MonogramAvatar from '@/components/MonogramAvatar';
import VoiceMeter from '@/components/dashboard/VoiceMeter';
import ClinicSearchPicker from '@/components/ClinicSearchPicker';
import ClinicMap from '@/components/ClinicMap';
import HospitalAttachmentsSection from '@/components/dashboard/HospitalAttachmentsSection';
import {
  Field, ShareUrlBar, PushNotificationToggle, RedeemCouponCard,
  VoiceAuthenticityNotice, ChangePasswordSection,
  WhatsNewLaunchBanner, SetupBlocker, WebsiteScoreWidget,
  TierBadgeWithUpgrade, UpgradeBanner, SubscriptionWarning,
} from '@/components/dashboard/widgets/SharedWidgets';

function CancellationModal({ booking, reasons, onClose, onDone }) {
  const [code, setCode] = useState('');
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const target = booking?._targetStatus || 'cancelled';
  const mustMessage = code === 'OTH-MSG';

  const submit = async () => {
    if (!code) { setError('Pick a reason.'); return; }
    if (mustMessage && !msg.trim()) { setError('Please add a message for "Other".'); return; }
    setSaving(true); setError('');
    try {
      await api.patch(`/dashboard/bookings/${booking.id}`, {
        status: target,
        reason_code: code,
        message: msg.trim(),
      });
      onDone();
    } catch (e) {
      setError(formatApiError(e));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="cancellation-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5 space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {target === 'rejected' ? 'Reject' : 'Cancel'} appointment
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {booking?.patient_name || 'Patient'} · {booking?.date} at {booking?.start_time}
          </p>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1.5 block">Reason *</label>
          <div className="grid grid-cols-1 gap-1.5 max-h-64 overflow-auto">
            {reasons.map(r => (
              <label key={r.code} className={`flex items-center gap-2 p-2 rounded-xl border text-sm cursor-pointer ${code === r.code ? 'border-sky-500 bg-sky-50' : 'border-slate-200 hover:border-sky-300'}`}>
                <input
                  type="radio"
                  name="reason"
                  value={r.code}
                  checked={code === r.code}
                  onChange={() => setCode(r.code)}
                  data-testid={`reason-${r.code}`}
                />
                <span className="flex-1 text-slate-700">{r.label}</span>
                <span className="text-[10px] font-mono text-slate-400">{r.code}</span>
              </label>
            ))}
          </div>
        </div>
        {mustMessage && (
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1.5 block">Message to patient *</label>
            <textarea
              value={msg}
              onChange={e => setMsg(e.target.value)}
              rows={3}
              maxLength={500}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none"
              data-testid="cancel-message"
              placeholder="Briefly explain what happened"
            />
          </div>
        )}
        {!mustMessage && (
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1.5 block">Extra note (optional)</label>
            <input
              type="text"
              value={msg}
              onChange={e => setMsg(e.target.value)}
              maxLength={300}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none"
              data-testid="cancel-message"
            />
          </div>
        )}
        <p className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-2">
          The patient will be notified by push + email with this reason. Doctor-initiated cancellations must be at least <b>1 hour</b> before the appointment start.
        </p>
        {error && <p className="text-sm text-red-600" data-testid="cancel-error">{error}</p>}
        <div className="flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-full text-sm font-medium text-slate-600 hover:bg-slate-100" data-testid="cancel-modal-close">
            Keep appointment
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="px-4 py-2 rounded-full bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50"
            data-testid="cancel-modal-submit"
          >
            {saving ? 'Cancelling…' : `${target === 'rejected' ? 'Reject' : 'Cancel'} & notify patient`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AppointmentsTab() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dnaTooltip, setDnaTooltip] = useState(null);
  const [cancelFor, setCancelFor] = useState(null); // booking object being cancelled
  const [reasons, setReasons] = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/dashboard/bookings').then(r => setBookings(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/meta/cancellation-reasons').then(r => setReasons(r.data)).catch(() => {});
  }, []);

  const handleAction = async (id, status) => {
    const target = bookings.find(b => b.id === id);
    if (status === 'cancelled' || status === 'rejected') {
      setCancelFor({ ...target, _targetStatus: status });
      return;
    }
    try {
      await api.patch(`/dashboard/bookings/${id}`, { status });
      load();
    } catch (e) { alert(formatApiError(e)); }
  };

  const handleComplete = async (id) => {
    await api.patch(`/dashboard/bookings/${id}`, { status: 'completed' });
    load();
  };

  const handleDNA = async (id) => {
    if (!window.confirm('Mark this patient as Did Not Attend? This affects their reliability score.')) return;
    await api.patch(`/dashboard/bookings/${id}/dna`);
    load();
  };

  if (loading) return <div className="text-center py-10"><Loader2 className="animate-spin text-sky-600 mx-auto" /></div>;

  // Smart sorting: group by today active → future → today completed → past completed → no-show
  const now = new Date();
  const todayStr = now.toISOString().split('T')[0];
  const currentMinutes = now.getHours() * 60 + now.getMinutes();

  const getTimeMinutes = (t) => {
    if (!t) return 0;
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
  };

  const categorize = (b) => {
    const isToday = b.date === todayStr;
    const isPast = b.date < todayStr;
    const isFuture = b.date > todayStr;
    const endMin = getTimeMinutes(b.end_time);
    const slotPassed = isToday && endMin <= currentMinutes;

    if (b.status === 'no_show' || b.dna) return 4; // No-shows always bottom
    if (b.status === 'completed') return 3; // Completed
    if (isPast) return 3; // Past confirmed = should have been completed
    if (isToday && !slotPassed) return 0; // Today upcoming = top priority
    if (isFuture) return 1; // Future appointments
    if (isToday && slotPassed) return 2; // Today, time passed but not completed
    return 1;
  };

  const sorted = [...bookings].sort((a, b) => {
    const catA = categorize(a);
    const catB = categorize(b);
    if (catA !== catB) return catA - catB;
    // Within same category, sort by date+time
    const dateA = `${a.date}_${a.start_time}`;
    const dateB = `${b.date}_${b.start_time}`;
    if (catA <= 1) return dateA.localeCompare(dateB); // Upcoming: earliest first
    return dateB.localeCompare(dateA); // Past: most recent first
  });

  const getCardStyle = (b) => {
    if (b.status === 'no_show' || b.dna) return 'bg-red-50 border-red-200';
    if (b.status === 'completed') return 'bg-emerald-50 border-emerald-200';
    if (b.status === 'rejected' || b.status === 'cancelled') return 'bg-slate-50 border-slate-200';
    const isToday = b.date === todayStr;
    const endMin = getTimeMinutes(b.end_time);
    const slotPassed = isToday && endMin <= currentMinutes;
    if (slotPassed && b.status === 'confirmed') return 'bg-amber-50 border-amber-200'; // Needs action
    return 'bg-white border-slate-200'; // Default
  };

  const getStatusBadge = (b) => {
    if (b.status === 'no_show' || b.dna) return { text: 'Did Not Attend', cls: 'bg-red-100 text-red-700' };
    if (b.status === 'completed') return { text: 'Consult Completed', cls: 'bg-emerald-100 text-emerald-700' };
    if (b.status === 'confirmed') {
      const isToday = b.date === todayStr;
      const endMin = getTimeMinutes(b.end_time);
      const slotPassed = isToday && endMin <= currentMinutes;
      if (slotPassed) return { text: 'Awaiting Update', cls: 'bg-amber-100 text-amber-700' };
      return { text: 'Confirmed', cls: 'bg-sky-100 text-sky-700' };
    }
    if (b.status === 'pending') return { text: 'Pending', cls: 'bg-amber-100 text-amber-700' };
    return { text: b.status, cls: 'bg-slate-100 text-slate-600' };
  };

  const showCompleteBtn = (b) => {
    if (b.status !== 'confirmed') return false;
    const isToday = b.date === todayStr;
    const isPast = b.date < todayStr;
    const startMin = getTimeMinutes(b.start_time);
    // Show if today and appointment time has started, or if past
    return isPast || (isToday && startMin <= currentMinutes);
  };

  const showDnaBtn = (b) => {
    if (b.status !== 'confirmed' || b.dna) return false;
    const isToday = b.date === todayStr;
    const isPast = b.date < todayStr;
    const endMin = getTimeMinutes(b.end_time);
    // Show if today and appointment time has passed, or if past
    return isPast || (isToday && endMin <= currentMinutes);
  };

  return (
    <div className="space-y-4" data-testid="appointments-tab">
      <h3 className="text-lg font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Your Appointments</h3>

      {/* Cancellation modal: structured reason code + optional message. */}
      {cancelFor && (
        <CancellationModal
          booking={cancelFor}
          reasons={reasons}
          onClose={() => setCancelFor(null)}
          onDone={() => { setCancelFor(null); load(); }}
        />
      )}
      {sorted.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-sm text-slate-500">No appointments yet.</div>
      ) : (
        sorted.map(b => {
          const badge = getStatusBadge(b);
          return (
            <div key={b.id} className={`rounded-2xl border p-5 transition-colors ${getCardStyle(b)}`} data-testid={`appt-${b.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    {b.status === 'completed' && <CheckCircle size={14} className="text-emerald-600" />}
                    {(b.status === 'no_show' || b.dna) && <UserX size={14} className="text-red-500" />}
                    <p className="font-medium text-slate-900 text-sm">{b.patient_name}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                    <span className="flex items-center gap-1"><Calendar size={11} /> {b.date}</span>
                    <span className="flex items-center gap-1"><Clock size={11} /> {b.start_time}-{b.end_time}</span>
                    <span className="flex items-center gap-1"><MapPin size={11} /> {b.location_name}</span>
                  </div>
                  {b.notes && <p className="text-xs text-slate-600">Notes: {b.notes}</p>}
                  <p className="text-xs text-slate-500">Type: <span className="capitalize font-medium">{b.booking_type}</span></p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${badge.cls}`} data-testid={`status-${b.id}`}>
                    {badge.text}
                  </span>
                  <div className="flex gap-1.5 items-center">
                    {b.status === 'pending' && (
                      <>
                        <button onClick={() => handleAction(b.id, 'confirmed')} className="p-1.5 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100" data-testid={`confirm-${b.id}`} title="Confirm">
                          <CheckCircle size={14} />
                        </button>
                        <button onClick={() => handleAction(b.id, 'rejected')} className="p-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100" data-testid={`reject-${b.id}`} title="Reject">
                          <XCircle size={14} />
                        </button>
                      </>
                    )}
                    {showCompleteBtn(b) && (
                      <button onClick={() => handleComplete(b.id)}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-[10px] font-medium hover:bg-emerald-700 transition-colors"
                        data-testid={`complete-${b.id}`}>
                        <CheckCircle size={12} /> Consult Completed
                      </button>
                    )}
                    {showDnaBtn(b) && (
                      <div className="relative">
                        <button onClick={() => handleDNA(b.id)}
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 text-[10px] font-medium border border-red-200"
                          data-testid={`dna-${b.id}`}>
                          <UserX size={12} /> DNA
                        </button>
                        {/* Info tooltip */}
                        <button
                          className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-slate-600 text-white text-[8px] font-bold flex items-center justify-center hover:bg-slate-700 cursor-help"
                          onMouseEnter={() => setDnaTooltip(b.id)}
                          onMouseLeave={() => setDnaTooltip(null)}
                          onClick={(e) => { e.stopPropagation(); setDnaTooltip(dnaTooltip === b.id ? null : b.id); }}
                          data-testid={`dna-info-${b.id}`}>
                          !
                        </button>
                        {dnaTooltip === b.id && (
                          <div className="absolute right-0 top-full mt-1 w-56 bg-slate-800 text-white text-[10px] leading-relaxed rounded-lg p-3 z-50 shadow-lg" data-testid={`dna-tooltip-${b.id}`}>
                            <p className="font-semibold mb-1">Did Not Attend (DNA)</p>
                            <p>Mark if the patient didn't show up for their appointment. This will:</p>
                            <ul className="mt-1 space-y-0.5 ml-2">
                              <li>- Reduce patient's reliability score</li>
                              <li>- Reduce patient reliability score</li>
                              <li>- Record for future reference</li>
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {/* Post-booking chat with patient (3 msgs/side cap) */}
              {(b.status === 'confirmed' || b.status === 'pending' || b.status === 'completed') && (
                <BookingChat bookingId={b.id} />
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

